import { useParams, Link, useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  FlagTriangleRight,
  MapPin,
  Users,
  User,
  Tent,
  ExternalLink,
  Pencil,
} from "lucide-react";
import { AttendeeStatus } from "@satyrsmc/shared/client";
import type {
  MemberEventDetail,
  MemberEventAttendee,
  MemberEventScheduleItem,
} from "@satyrsmc/shared/dto/member/event";
import { formatDateLong, formatDate, formatWeekdayMonthDay } from "@/lib/date-utils";
import { useMemberEventDetail } from "@/queries/member-events";
import { useEventGallery } from "@/queries/event-gallery";
import { trpc } from "@/trpc";
import { useAuth } from "@/state/AuthContext";
import { EVENT_TYPE_LABELS, EVENT_ASSIGNMENT_CATEGORY_LABELS } from "@/lib/event-constants";
import { getMapEmbedUrl, getStaticMapUrl } from "@/lib/maps";
import { resolveEventLocationDisplay, resolveEventUrlDisplay } from "@/lib/event-location";
import { getEventRegistrationBlock, isPastRefundCutoff, isEventPast } from "@/lib/eventUtils";
import { ENV } from "@/env";
import { Badge } from "@/components/ui/badge";
import { Button, SafeHtml } from "@satyrsmc/shared/components";
import { Skeleton } from "@/components/ui/skeleton";
import { EventGallerySection } from "./EventGallerySection";

const GOOGLE_MAPS_API_KEY = ENV.GOOGLE_MAPS_API_KEY || undefined;

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const hours = d.getUTCHours();
  const minutes = d.getUTCMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  const m = String(minutes).padStart(2, "0");
  return `${h}:${m} ${ampm}`;
}

/**
 * Group schedule items into chronological per-day sections. The day key is the
 * UTC date portion of the timestamp, matching {@link formatTime}'s UTC display,
 * so grouping stays timezone-safe. Days and items within a day are ordered
 * chronologically. Legacy items with no time are collected into a trailing
 * `day: null` group so they surface as "Unscheduled" rather than being dropped.
 */
function groupScheduleByDay(
  items: MemberEventScheduleItem[],
): { day: string | null; items: MemberEventScheduleItem[] }[] {
  const byDay = new Map<string, MemberEventScheduleItem[]>();
  const unscheduled: MemberEventScheduleItem[] = [];
  for (const item of items) {
    if (!item.scheduled_time) {
      unscheduled.push(item);
      continue;
    }
    const key = item.scheduled_time.slice(0, 10);
    const arr = byDay.get(key) ?? [];
    arr.push(item);
    byDay.set(key, arr);
  }
  const days = [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, dayItems]) => ({
      day: day as string | null,
      items: [...dayItems].sort((a, b) =>
        (a.scheduled_time ?? "").localeCompare(b.scheduled_time ?? ""),
      ),
    }));
  if (unscheduled.length > 0) days.push({ day: null, items: unscheduled });
  return days;
}

// ─── Loading skeleton ────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <PageContainer>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-10 w-3/4" />
      <Skeleton className="aspect-[16/7] w-full rounded-lg" />
      <div className="flex gap-8">
        <div className="flex-1 space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
        <div className="hidden lg:block w-[35%] space-y-4">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    </PageContainer>
  );
}

// ─── Attendee card ───────────────────────────────────────────────────────────

function AttendeeCard({ attendee }: { attendee: MemberEventAttendee }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="size-10 shrink-0 rounded-full overflow-hidden bg-muted flex items-center justify-center">
        {attendee.photo_url ? (
          <img src={attendee.photo_url} alt="" className="size-full object-cover" />
        ) : (
          <User className="size-5 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium truncate">
          {attendee.display_name}
          {attendee.attendance_date && (
            <span className="ml-1 font-normal text-muted-foreground">
              ({formatWeekdayMonthDay(attendee.attendance_date)})
            </span>
          )}
        </p>
        <p className="text-sm text-muted-foreground">{attendee.is_member ? "Member" : "Guest"}</p>
      </div>
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ event }: { event: MemberEventDetail }) {
  const navigate = useNavigate();
  // Once an event is over the card stops asking about registration and just
  // records whether you were there.
  const eventIsPast = isEventPast({
    status: event.status,
    start_date: event.start_date,
    end_date: event.end_date,
  });
  // Same query EventGallerySection runs on this page — react-query dedupes it,
  // so this only reads the gallery id the section already fetched.
  const { data: gallery } = useEventGallery(event.id, eventIsPast);
  const galleryId = gallery?.gallery_id ?? null;
  const utils = trpc.useUtils();
  // Photo submission lives on the gallery page; for an event with no gallery
  // yet, create it first, then head there.
  const ensureGallery = trpc.members.gallery.ensureForEvent.useMutation({
    onSuccess: async (result) => {
      await utils.members.gallery.getByEvent.invalidate({ event_id: event.id });
      navigate(`/gallery/${result.gallery_id}`);
    },
  });
  const hostAttendees = event.attendees.filter((a) => event.host_ids.includes(a.contact_id));

  const locationDisplay = resolveEventLocationDisplay(
    event.event_location,
    event.event_location_link,
  );
  const mapEmbedUrl = event.event_location_embed ?? getMapEmbedUrl(event.event_location_link);
  const staticMapUrl =
    locationDisplay?.kind === "text" && !mapEmbedUrl
      ? getStaticMapUrl(locationDisplay.text, GOOGLE_MAPS_API_KEY ?? null)
      : null;

  const hasExistingRsvp = event.my_rsvp !== null;
  const registrationBlock = getEventRegistrationBlock({
    status: event.status,
    start_date: event.start_date,
    end_date: event.end_date,
  });
  // Cancelling a "Yes" RSVP is locked out past the refund cutoff — switching
  // from "No"/no RSVP to "Yes" isn't a cancellation, so it stays unaffected.
  const cancelLocked =
    event.my_rsvp === AttendeeStatus.Yes && isPastRefundCutoff(event.refund_cutoff_date);

  return (
    <div className="space-y-4">
      {/* RSVP CTA */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        {eventIsPast ? (
          <>
            <p className="text-sm font-medium text-muted-foreground">Did you go?</p>
            <div className="text-sm">
              <Badge variant={event.my_rsvp === AttendeeStatus.Yes ? "success" : "secondary"}>
                {event.my_rsvp === AttendeeStatus.Yes ? "Went" : "Didn't Go"}
              </Badge>
            </div>
            {event.can_contribute && (
              <>
                <Button className="w-full" asChild>
                  <Link to={`/events/${event.id}/feedback`}>How was it?</Link>
                </Button>
                {galleryId ? (
                  <Button className="w-full" asChild>
                    <Link to={`/gallery/${galleryId}`}>Submit Photos</Link>
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    disabled={ensureGallery.isPending}
                    onClick={() => ensureGallery.mutate({ event_id: event.id })}
                  >
                    Submit Photos
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-muted-foreground">Are you going?</p>
            {hasExistingRsvp && (
              <div className="text-sm">
                Status:{" "}
                <Badge variant={event.my_rsvp === AttendeeStatus.Yes ? "success" : "destructive"}>
                  {event.my_rsvp === AttendeeStatus.Yes ? "Going" : "Not Going"}
                </Badge>
              </div>
            )}
            {registrationBlock.blocked ? (
              <p className="text-sm text-muted-foreground">{registrationBlock.reason}</p>
            ) : cancelLocked ? (
              <>
                <Button className="w-full" disabled>
                  Change RSVP
                </Button>
                <p className="text-sm text-muted-foreground">
                  RSVPs cannot be cancelled after {formatDate(event.refund_cutoff_date!)}.
                </p>
              </>
            ) : (
              <Button className="w-full" asChild>
                <Link to={`/events/${event.id}/register`}>
                  {hasExistingRsvp ? "Change RSVP" : "RSVP Now"}
                </Link>
              </Button>
            )}
          </>
        )}
      </div>

      {/* Host row(s) */}
      {hostAttendees.map((host) => (
        <div key={host.contact_id} className="flex items-center gap-3">
          <div className="size-8 shrink-0 rounded-full overflow-hidden bg-muted flex items-center justify-center">
            {host.photo_url ? (
              <img src={host.photo_url} alt="" className="size-full object-cover" />
            ) : (
              <User className="size-4 text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{host.display_name}</p>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Host
          </span>
        </div>
      ))}

      {/* Event meta */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span title="Event type">
            <Tent className="size-4 text-muted-foreground shrink-0" />
          </span>
          <Badge variant="secondary">{EVENT_TYPE_LABELS[event.event_type]}</Badge>
        </div>

        {event.start_date && (
          <div className="flex items-center gap-2 text-sm">
            <span title="Date">
              <Calendar className="size-4 text-muted-foreground shrink-0" />
            </span>
            <span>
              {formatDate(event.start_date)}
              {event.end_date && ` – ${formatDate(event.end_date)}`}
            </span>
          </div>
        )}

        {locationDisplay && (
          <div className="space-y-1">
            <div className="flex items-start gap-2 text-sm">
              <span title="Location">
                <MapPin className="size-4 text-muted-foreground shrink-0 mt-0.5" />
              </span>
              {locationDisplay.kind === "text" ? (
                <span className="truncate">{locationDisplay.text}</span>
              ) : (
                <a
                  href={locationDisplay.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate hover:underline"
                >
                  {locationDisplay.label}
                </a>
              )}
            </div>
            {locationDisplay.kind === "text" && locationDisplay.linkUrl && (
              <a
                href={locationDisplay.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-6 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider hover:underline"
              >
                Directions
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        )}

        {event.start_location && (
          <div className="flex items-start gap-2 text-sm">
            <span title="Start location">
              <MapPin className="size-4 text-muted-foreground shrink-0 mt-0.5" />
            </span>
            <span>{event.start_location}</span>
          </div>
        )}

        {event.end_location && (
          <div className="flex items-start gap-2 text-sm">
            <span title="End location">
              <FlagTriangleRight className="size-4 text-muted-foreground shrink-0 mt-0.5" />
            </span>
            <span>{event.end_location}</span>
          </div>
        )}

        {event.ride_cost != null && (
          <div className="flex items-center gap-2 text-sm">
            <span title="Ride cost">
              <DollarSign className="size-4 text-muted-foreground shrink-0" />
            </span>
            <span>${event.ride_cost.toLocaleString()} ride cost</span>
          </div>
        )}

        {event.facebook_event_url && (
          <div className="flex items-center gap-2 text-sm">
            <span title="Facebook event">
              <ExternalLink className="size-4 text-muted-foreground shrink-0" />
            </span>
            <a
              href={event.facebook_event_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Facebook event
            </a>
          </div>
        )}
      </div>

      {staticMapUrl && (
        <div className="overflow-hidden rounded-lg border border-border">
          <img
            src={staticMapUrl}
            alt={locationDisplay?.kind === "text" ? `Map of ${locationDisplay.text}` : "Map"}
            className="w-full"
            loading="lazy"
          />
        </div>
      )}
      {mapEmbedUrl && (
        <div className="overflow-hidden rounded-lg border border-border">
          <iframe
            title="Event map"
            src={mapEmbedUrl}
            width="100%"
            height="200"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function MemberEventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: event, isLoading, error } = useMemberEventDetail(id!);
  const { isAdmin } = useAuth();

  if (isLoading) return <DetailSkeleton />;

  if (error || !event) {
    return (
      <PageContainer>
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to Events
        </Link>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Event not found.
        </div>
      </PageContainer>
    );
  }

  const hostContactIds = new Set(event.host_ids);
  const hosts = event.attendees.filter((a) => hostContactIds.has(a.contact_id));
  const nonHostAttendees = event.attendees.filter((a) => !hostContactIds.has(a.contact_id));

  const heroPhotoUrl = isEventPast(event)
    ? (event.photos[0]?.photo_url ?? event.poster_url)
    : event.poster_url;

  return (
    <PageContainer>
      {/* Back link */}
      <Link
        to="/events"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="size-4" />
        Back to Events
      </Link>

      {/* Eyebrow date */}
      {event.start_date && (
        <p className="text-sm text-muted-foreground">{formatDateLong(event.start_date)}</p>
      )}

      {/* Title + admin actions */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-3xl font-bold">{event.name}</h1>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/events/${id}`}>
                <Pencil className="size-3.5" />
                Edit
              </Link>
            </Button>
          </div>
        )}
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left column */}
        <div className="flex-1 lg:basis-[65%] space-y-6 min-w-0">
          {/* Hero image */}
          {heroPhotoUrl ? (
            <img
              src={heroPhotoUrl}
              alt=""
              className="w-full aspect-[16/7] object-cover rounded-lg"
            />
          ) : (
            <div className="w-full aspect-[16/7] rounded-lg bg-muted flex items-center justify-center">
              <Calendar className="size-12 text-muted-foreground/30" />
            </div>
          )}

          {/* Description */}
          {event.description && (
            <SafeHtml
              html={event.description}
              className="prose prose-sm max-w-none text-foreground [&_strong]:font-semibold [&_a]:text-[var(--color-link)] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:mb-3 [&_p:last-child]:mb-0"
            />
          )}

          {/* Fee / CTA */}
          {(() => {
            const urlDisplay = resolveEventUrlDisplay(event.event_url, event.event_url_text);
            const hasFees = event.ga_ticket_cost != null || event.day_pass_cost != null;
            if (!hasFees && !urlDisplay && !event.refund_cutoff_date) return null;
            return (
              <div className="space-y-1">
                {event.ga_ticket_cost != null && (
                  <p className="font-semibold">
                    GA Ticket: ${event.ga_ticket_cost.toLocaleString()}
                  </p>
                )}
                {event.day_pass_cost != null && (
                  <p className="font-semibold">Day pass: ${event.day_pass_cost.toLocaleString()}</p>
                )}
                {event.refund_cutoff_date && (
                  <p className="text-sm text-muted-foreground">
                    RSVPs cannot be cancelled after {formatDate(event.refund_cutoff_date)}.
                  </p>
                )}
                {urlDisplay && (
                  <div className="pt-1">
                    <a
                      href={urlDisplay.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      {urlDisplay.label}
                      <ExternalLink className="size-3.5" />
                    </a>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Schedule */}
          {event.schedule_items.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold">Schedule</h2>
              {groupScheduleByDay(event.schedule_items).map(({ day, items }) => (
                <div key={day ?? "unscheduled"} className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    {day ? formatDateLong(day) : "Unscheduled"}
                  </h3>
                  {items.map((item) => (
                    <div key={item.id} className="flex items-start gap-3 text-sm">
                      {item.scheduled_time && (
                        <span className="font-medium whitespace-nowrap text-muted-foreground shrink-0">
                          {formatTime(item.scheduled_time)}
                        </span>
                      )}
                      <div>
                        <p className="font-medium">{item.label}</p>
                        {item.location && <p className="text-muted-foreground">{item.location}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </section>
          )}

          {/* Your Assignments */}
          {event.my_assignments.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-lg font-semibold">Your Assignments</h2>
              <div className="divide-y divide-border">
                {event.my_assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between gap-3 py-2 text-sm"
                  >
                    <span className="font-medium">{assignment.name}</span>
                    <Badge variant="outline">
                      {EVENT_ASSIGNMENT_CATEGORY_LABELS[assignment.category]}
                    </Badge>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Attendees */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Attendees</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm">
                <Users className="size-3.5" />
                <span className="font-bold">{event.rsvp_yes_count}</span>
                <span className="text-muted-foreground">attendees</span>
              </span>
            </div>

            {/* Hosts */}
            {hosts.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Host{hosts.length > 1 ? "s" : ""}
                </p>
                <div className="divide-y divide-border">
                  {hosts.map((host) => (
                    <AttendeeCard key={host.contact_id} attendee={host} />
                  ))}
                </div>
              </div>
            )}

            {/* Attendees */}
            {nonHostAttendees.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Attendees
                </p>
                <div className="divide-y divide-border">
                  {nonHostAttendees.map((attendee) => (
                    <AttendeeCard key={attendee.contact_id} attendee={attendee} />
                  ))}
                </div>
              </div>
            )}

            {event.rsvp_yes_count === 0 && (
              <p className="text-sm text-muted-foreground">No one has RSVP'd yet.</p>
            )}
          </section>

          {/* Gallery — only past events have one, and only once it has photos */}
          {isEventPast(event) && <EventGallerySection eventId={event.id} />}
        </div>

        {/* Right sidebar */}
        <div className="lg:basis-[35%] order-first lg:order-none lg:sticky lg:top-20 lg:self-start">
          <Sidebar event={event} />
        </div>
      </div>
    </PageContainer>
  );
}
